import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import teamsService from '../services/teamsService';
import { workspaceService } from '../services/workspaceService';
import {
  canRegenerateInviteCode,
  inviteRegenerateCooldownMessage,
} from '../utils/inviteCodeCooldown';
import TeamInviteModal from './TeamInviteModal';
import { useTeamVaultStore, selectTeamEntry, loadTeamVault } from '../store/teamVaultStore';
import { canAdministerGrants } from '../utils/teamVaultGrants';
import { ConfirmModal } from './ui/Modal';
import { DARK, LIGHT, useDark } from './teams/teamsTheme';
import { getTeamsCache, saveTeamsCache } from './teams/teamsCache';
import { teamsViews } from './teams/teamsViews';
import { useTeamsLoaders } from './teams/useTeamsLoaders';
export { invalidateTeamDetailWorkspacesCache, tabsForTeam } from './teams/teamsCache';

const Teams = ({
  onOpenModal,
  currentUserId,
  teams: teamsProp = [],
  onTeamsChange,
  showToast,
  workspaces = [],
  onOpenWorkspace, onShareWithTeam, onForkWorkspace, onOpenBoard,
  openDetailTeamIdFromPalette = null,
  onConsumedOpenDetailTeamIdFromPalette,
  teamLibraryRefresh = null,
}) => {
  const { t } = useTranslation();
  const isDark = useDark();
  const T = isDark ? DARK : LIGHT;

  const [loading, setLoading]       = useState(() => teamsProp.length === 0 && !getTeamsCache());
  const [error, setError]           = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [uniqueMembers, setUniqueMembers] = useState(0);

  const [detailTeamId, setDetailTeamId] = useState(null);
  const [activeTab, setActiveTab]       = useState('Overview');

  const [members, setMembers]           = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState(null);
  const [invitations, setInvitations]   = useState([]);
  const [invLoading, setInvLoading]     = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [teamWs, setTeamWs]         = useState([]);
  const [wsLoading, setWsLoading]   = useState(false);

  const [teamBoards, setTeamBoards]   = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);

  const [activities, setActivities]     = useState([]);
  const [actLoading, setActLoading]     = useState(false);
  const [actSearch, setActSearch]       = useState('');
  const [actFilter, setActFilter]       = useState(null);

  const [headerMenu, setHeaderMenu]     = useState(false);
  const [memberRowMenu, setMemberRowMenu] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const selectAllRef = useRef(null);
  const headerMenuRef = useRef(null);
  const [confirm, setConfirm] = useState({ open:false, type:null, team:null, member:null, title:'', message:'', confirmText:'' });

  const teams = useMemo(() => teamsProp || [], [teamsProp]);

  const activeTeam = useMemo(
    ()=> detailTeamId!=null ? teams.find(t=>t.id===detailTeamId)||null : null,
    [teams, detailTeamId]
  );

  // Team vault: the SERVER decides who may administer grants. Subscribed to the
  // raw slice and narrowed through useMemo — `selectTeamEntry` returns the
  // stored object (or one frozen shared empty), so the reference is stable and
  // this cannot loop the panel the way a fresh-object selector would.
  const teamVaults = useTeamVaultStore((s) => s.byTeam);
  const teamVaultEntry = useMemo(
    () => selectTeamEntry(teamVaults, activeTeam?.id),
    [teamVaults, activeTeam?.id]
  );
  const canAdministerVault = canAdministerGrants(teamVaultEntry);
  useEffect(() => {
    if (activeTeam?.id != null && !teamVaultEntry.loaded) loadTeamVault(activeTeam.id);
  }, [activeTeam?.id, teamVaultEntry.loaded]);

  const isOwner = t => t?.is_owner || t?.owner?.id===currentUserId;
  const isAdmin = t => isOwner(t) || t?.role==='admin';
  const canInvite = t => isOwner(t) || ['admin','owner'].includes(t?.role);
  const canShareToTeam = t => isOwner(t) || ['admin','owner','editor'].includes(t?.role);

  const {
    loadTeams, loadMembers, loadInvitations, loadWs, loadBoards, loadActivity,
  } = useTeamsLoaders({
    onTeamsChange, setLoading, setError, setUniqueMembers, setMembers, setMembersLoading,
    setInvitations, setInvLoading, setTeamWs, setWsLoading, setTeamBoards, setBoardsLoading,
    setActivities, setActLoading, actFilter, activeTeam, isAdmin,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: loadTeams calls onTeamsChange, which the parent re-creates each render, so tracking it would refetch forever
  useEffect(()=>{ loadTeams(); }, []);
  useEffect(()=>{
    if (detailTeamId!=null && !teams.some(t=>t.id===detailTeamId)) setDetailTeamId(null);
  }, [teams, detailTeamId]);
  useEffect(()=>{
    if (!openDetailTeamIdFromPalette) return;
    if (teams.some(t=>Number(t.id)===Number(openDetailTeamIdFromPalette))) {
      setDetailTeamId(openDetailTeamIdFromPalette); onConsumedOpenDetailTeamIdFromPalette?.();
    } else if (teams.length) onConsumedOpenDetailTeamIdFromPalette?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the palette request is the trigger; the consume callback is unmemoized by the parent, so tracking it would re-fire the ack on every render
  }, [openDetailTeamIdFromPalette, teams]);
  useEffect(()=>{
    if (!activeTeam) return;
    setSelectedMembers(new Set());
    loadMembers(); loadInvitations(); loadActivity(); loadWs(); loadBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loaders already key on activeTeam.id; adding the team object would refetch all five endpoints after any edit to it (e.g. a rename)
  }, [loadMembers, loadInvitations, loadActivity, loadWs, loadBoards]);
  useEffect(() => {
    if (!teamLibraryRefresh?.teamId || !activeTeam?.id) return;
    if (Number(activeTeam.id) !== Number(teamLibraryRefresh.teamId)) return;
    loadWs();
    loadBoards();
    loadActivity();
  }, [teamLibraryRefresh, activeTeam?.id, loadWs, loadBoards, loadActivity]);
  // Reload activity when the team itself is externally updated (e.g. after edit modal saves)
  useEffect(()=>{ if (activeTeam?.id) loadActivity(); }, [activeTeam?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if (!headerMenu) return;
    const h = e=>{ if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setHeaderMenu(false); };
    document.addEventListener('mousedown', h); return ()=>document.removeEventListener('mousedown', h);
  }, [headerMenu]);
  // TeamMemberRowMenu owns its outside-click and scroll closing.
  const closeMemberRowMenu = useCallback(()=>setMemberRowMenu(null), []);

  // ── actions ───────────────────────────────────────────────────────────────────
  const openTeam = team => { setDetailTeamId(team.id); setActiveTab('Overview'); };
  const copyCode = async team => {
    try { await navigator.clipboard.writeText(team.invite_code); showToast?.('Invite code copied!','success'); }
    catch { showToast?.('Failed to copy','error'); }
    setHeaderMenu(false);
  };

  const ask = opts => setConfirm({ open:true, ...opts });
  const closeAsk = ()=>setConfirm(c=>({...c, open:false}));
  const doConfirm = async () => {
    const { type, team, member } = confirm; closeAsk();
    try {
      if (type==='leave')           { await teamsService.leaveTeam(team.id); if (detailTeamId===team.id) setDetailTeamId(null); loadTeams(); showToast?.(`Left "${team.name}"`, 'success'); }
      else if (type==='delete')     { await teamsService.deleteTeam(team.id); if (detailTeamId===team.id) setDetailTeamId(null); loadTeams(); showToast?.(`"${team.name}" deleted`,'success'); }
      else if (type==='regenerate') {
        if (!canRegenerateInviteCode(team)) {
          showToast?.(inviteRegenerateCooldownMessage(team), 'error');
          return;
        }
        const res = await teamsService.regenerateInviteCode(team.id);
        const newCode = res?.invite_code;
        if (newCode) {
          const updated = teams.map(t =>
            t.id === team.id
              ? {
                  ...t,
                  invite_code: newCode,
                  invite_code_regenerated_at: res.invite_code_regenerated_at || new Date().toISOString(),
                }
              : t
          );
          onTeamsChange(updated);
          saveTeamsCache(updated, uniqueMembers);
        }
        loadActivity();
        showToast?.('Invite code regenerated','success');
      }
      else if (type==='removeMember') { await teamsService.removeMember(team.id,member.id); loadMembers(); loadTeams(); loadActivity(); showToast?.(`${member.name} removed`,'success'); }
      else if (type==='bulkRemove')   { await Promise.all(member.map(m=>teamsService.removeMember(team.id,m.id))); setSelectedMembers(new Set()); loadMembers(); loadTeams(); loadActivity(); showToast?.(`${member.length} member${member.length>1?'s':''} removed`,'success'); }
      else if (type==='cancelInv')  { await teamsService.cancelInvitation(team.id,member.id); loadInvitations(); loadActivity(); showToast?.('Invitation cancelled','success'); }
      else if (type==='removeFromTeam') { await workspaceService.removeFromTeam(team.id, member.id); loadWs(); loadTeams(); loadActivity(); showToast?.(`"${member.name}" removed from team library`,'success'); }
      else if (type==='removeBoardFromTeam') { await teamsService.removeTeamBoard(team.id, member.id); loadBoards(); loadActivity(); showToast?.(`"${member.name}" removed from team boards`,'success'); }
    } catch(e) { showToast?.(`Failed: ${e.message}`,'error'); }
  };

  const changeRole = async (member, role) => {
    if (!activeTeam) return;
    try { await teamsService.updateMemberRole(activeTeam.id,member.id,role); loadMembers(); loadActivity(); showToast?.(`Role updated to ${role}`,'success'); }
    catch(e) { showToast?.(e.message,'error'); }
  };
  const resendInv = async inv => {
    try { await teamsService.resendInvitation(activeTeam.id,inv.id); loadInvitations(); loadActivity(); showToast?.('Resent','success'); }
    catch(e) { showToast?.(e.message,'error'); }
  };

  // ── filtered data ─────────────────────────────────────────────────────────────
  const listTeams = useMemo(()=>{
    let l = teams;
    if (searchTerm) { const q=searchTerm.toLowerCase(); l=l.filter(t=>t.name.toLowerCase().includes(q)||t.description?.toLowerCase().includes(q)); }
    if (roleFilter==='owned')  l=l.filter(t=>isOwner(t));
    else if (roleFilter==='admin')  l=l.filter(t=>!isOwner(t)&&t.role==='admin');
    else if (roleFilter==='member') l=l.filter(t=>!isOwner(t)&&t.role!=='admin');
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isOwner is redeclared each render but reads only currentUserId, which is already tracked
  }, [teams, searchTerm, roleFilter, currentUserId]);

  const filteredMembers = useMemo(()=>{
    let l = members;
    if (memberSearch) { const q=memberSearch.toLowerCase(); l=l.filter(m=>m.name?.toLowerCase().includes(q)||m.email?.toLowerCase().includes(q)); }
    if (memberRoleFilter) l=l.filter(m=>m.role===memberRoleFilter);
    return l;
  }, [members, memberSearch, memberRoleFilter]);

  const filteredAct = useMemo(()=>{
    if (!actSearch) return activities;
    const q = actSearch.toLowerCase();
    return activities.filter(a=>{
      const m = a.metadata||{};
      return (a.user?.name||'').toLowerCase().includes(q)||Object.values(m).some(v=>String(v).toLowerCase().includes(q));
    });
  }, [activities, actSearch]);

  const meRole = members.find(m=>m.id===currentUserId)?.role;
  const canChangeRole = m => meRole==='owner' && m.id!==currentUserId && m.role!=='owner';
  const canRemove = m => {
    if (m.id===currentUserId||m.role==='owner') return false;
    return meRole==='owner'||(meRole==='admin'&&!['owner','admin'].includes(m.role));
  };

  const { renderHub, renderDetail } = teamsViews({
    t, T, teams, onOpenModal, showToast, onOpenWorkspace, onShareWithTeam, onForkWorkspace,
    onOpenBoard, isDark, loading, error, searchTerm, setSearchTerm, roleFilter, setRoleFilter,
    uniqueMembers, setDetailTeamId, activeTab, setActiveTab, members, membersLoading, memberSearch,
    setMemberSearch, memberRoleFilter, setMemberRoleFilter, invitations, invLoading,
    setShowInviteModal, teamWs, wsLoading, teamBoards, boardsLoading, activities, actLoading,
    actSearch, setActSearch, actFilter, setActFilter, headerMenu, setHeaderMenu, memberRowMenu,
    setMemberRowMenu, selectedMembers, setSelectedMembers, selectAllRef, headerMenuRef, activeTeam,
    canAdministerVault, isOwner, isAdmin, canInvite, canShareToTeam, loadTeams, loadInvitations,
    closeMemberRowMenu, openTeam, copyCode, ask, changeRole, resendInv, listTeams, filteredMembers,
    filteredAct, canChangeRole, canRemove,
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // ROOT RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <>
      <div style={{display:'flex', height:'100%', width:'100%', minHeight:0, minWidth:0}}>
        {detailTeamId==null||!activeTeam ? renderHub() : renderDetail()}
      </div>
      {confirm.open && (
        <ConfirmModal
          isOpen={confirm.open} onClose={closeAsk} onConfirm={doConfirm}
          title={confirm.title} message={confirm.message} confirmText={confirm.confirmText}
          type={['delete','removeMember','cancelInv','removeFromTeam','removeBoardFromTeam'].includes(confirm.type)?'danger':'warning'}
          hideCancel={confirm.type === 'delete' || confirm.type === 'regenerate'}
        />
      )}
      {showInviteModal && activeTeam && (
        <TeamInviteModal
          isOpen={showInviteModal}
          onClose={()=>{ setShowInviteModal(false); loadTeams(); loadMembers(); loadInvitations(); loadActivity(); }}
          team={activeTeam}
          showToast={showToast}
          onTeamUpdated={(patch) => {
            const updated = teams.map((t) => (t.id === activeTeam.id ? { ...t, ...patch } : t));
            onTeamsChange(updated);
            saveTeamsCache(updated, uniqueMembers);
            loadActivity();
          }}
        />
      )}
    </>
  );
};

export default Teams;
